import type { FinanceQuoteLineItem } from "~/types/finance";

import { FINANCE_PERCENT_MAX } from "~/constants";

/** Display value for numeric line-item inputs (empty string when unset). */
export const formatLineItemNumberInput = (
  value: number | null | undefined,
): string => (value === null || value === undefined ? "" : String(value));

/** Parse numeric line-item input; empty field stays unset while editing. */
export const parseLineItemNumberInput = (raw: string): number | undefined => {
  if (raw === "" || raw === "-") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

export const parseLineItemPercentInput = (raw: string): number | undefined => {
  const n = parseLineItemNumberInput(raw);
  if (n === undefined) return undefined;
  return clampPercent(n);
};

/** Markup has no upper cap — only reject negatives / non-finite. */
export const parseLineItemMarkupPercentInput = (
  raw: string,
): number | undefined => {
  const n = parseLineItemNumberInput(raw);
  if (n === undefined) return undefined;
  return clampMarkupPercent(n);
};

/** Clamp discount percent to 0–100. */
export const clampPercent = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > FINANCE_PERCENT_MAX) return FINANCE_PERCENT_MAX;
  return n;
};

/** Markup percent: allow any non-negative value. */
export const clampMarkupPercent = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
};

export const calculateLineItemTotal = (item: {
  quantity?: number | null;
  unitPrice?: number | null;
  discountPercent?: number | null;
  markupPercent?: number | null;
}): number => {
  const qty = Number(item.quantity) || 0;
  const price = Number(item.unitPrice) || 0;
  const discount = clampPercent(item.discountPercent);
  const markup = clampMarkupPercent(item.markupPercent);
  const base = qty * price;
  const afterMarkup = base * (1 + markup / 100);
  return Math.round(afterMarkup * (1 - discount / 100) * 100) / 100;
};

/** Unit price with markup folded in — used on public URL and PDF (markup column is hidden). */
export const customerFacingUnitPrice = (item: {
  unitPrice?: number | null;
  markupPercent?: number | null;
}): number => {
  const price = Number(item.unitPrice) || 0;
  const markup = clampMarkupPercent(item.markupPercent);
  return Math.round(price * (1 + markup / 100) * 100) / 100;
};

export const withComputedLineItemTotals = (
  items: FinanceQuoteLineItem[],
): FinanceQuoteLineItem[] =>
  items.map((row) => ({
    ...row,
    total: calculateLineItemTotal(row),
  }));

/** Stable key so local rows resync when server line items change, not on array identity. */
export const financeLineItemsSyncKey = (
  items?: FinanceQuoteLineItem[] | null,
) =>
  (items ?? [])
    .map((item) =>
      [
        item._id ?? "",
        item.name ?? "",
        item.quantity ?? "",
        item.unit ?? "",
        item.unitPrice ?? "",
        item.discountPercent ?? "",
        item.markupPercent ?? "",
      ].join(":"),
    )
    .join("|");

export const createEmptyLineItem = (): FinanceQuoteLineItem => ({
  name: "",
  quantity: 1,
  unit: "EACH",
  unitPrice: 0,
  discountPercent: 0,
  markupPercent: 0,
  total: 0,
});

/** Payload shape for create/update finance quote line items. */
const toPersistedLineItemInput = (row: FinanceQuoteLineItem) => ({
  _id: row._id ?? undefined,
  name: row.name ?? "",
  quantity: row.quantity ?? 0,
  unit: row.unit ?? "EACH",
  unitPrice: row.unitPrice ?? 0,
  discountPercent: row.discountPercent ?? 0,
  markupPercent: row.markupPercent ?? 0,
  total: row.total ?? 0,
  packageId: row.packageId ?? undefined,
  packageName: row.packageName ?? undefined,
});

const defaultQuoteLineItemsOnCreate = () =>
  toPersistedLineItemInput(
    withComputedLineItemTotals([createEmptyLineItem()])[0],
  );

export const defaultQuoteLineItemsOnCreateList = () => [
  defaultQuoteLineItemsOnCreate(),
];

/** Quote / invoice detail line-item table — keep header + rows on the same grid. */
export const FINANCE_LINE_ITEMS_GRID =
  "grid grid-cols-[minmax(0,1.6fr)_72px_110px_110px_96px_96px_110px_36px] gap-sm items-center px-md [&>*]:min-w-0";

export const FINANCE_LINE_ITEM_HEADER_CELL = [
  "text-left",
  "text-left pl-sm",
  "text-left pl-sm",
  "text-left pl-sm",
  "text-left pl-sm",
  "text-left pl-sm",
  "text-left pl-sm",
  "",
] as const;
